import React, { useState } from "react";
import { useRecoilState } from "recoil";
import { Button } from "../shared/Button/Button";
import ClinicalIndication from "./NewReqClinicalIndication";
import PatientInformation from "./NewReqPatientInformation";
import {
  PreFlightCheckButtons,
  PreFlightWrapper,
} from "./NewRequisition.styles";
import NewReqBillingInformation from "./NewReqBillingInformation";
import NewReqShippingInformation from "./NewReqShippingInformation";
import {
  currentEditDraftRequisition,
  currentNewRequisition,
  orderedRequisitions,
} from "../../recoil/atoms";
import { convertNewReqFormToRequest } from "./Services/helpers";
import {
  generateTreatmentNotesNewOrder,
  submitNewRequisitionOrder,
} from "./Services/NewReqService";
import { RefreshNewRequisitionOrders } from "../Grid/Services/GridService";
import { NewRequisitionType, StepperIndex } from "./types";
import { getInitialNewRequisitionForm } from "../../recoil/helpers";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

type IProps = {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setSubmmittedRequisition: React.Dispatch<
    React.SetStateAction<NewRequisitionType | undefined>
  >;
  setIsSubmitError: React.Dispatch<React.SetStateAction<boolean>>;
  setTab: () => void;
};

const PreflightCheck: React.FC<IProps> = ({
  setActiveStep,
  setSubmmittedRequisition,
  setTab,
  setIsSubmitError,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [newGlobalRequisition, setGlobalNewRequisition] = useRecoilState(
    currentNewRequisition,
  );
  const [, setAllOrderedRequisitions] = useRecoilState(orderedRequisitions);
  const [, setDraftEditReq] = useRecoilState(currentEditDraftRequisition);
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { enabled: shipKitEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ShipKitToPatient,
  );

  const handleSubmit = async () => {
    if (auth) {
      const convertedFormToType = convertNewReqFormToRequest(
        newGlobalRequisition,
        shipKitEnabled,
      );

      try {
        setIsSubmitError(false);
        setSubmitLoading(true);
        if (convertedFormToType.treatment_notes) {
          const treatmentNoteURL = await generateTreatmentNotesNewOrder(
            convertedFormToType.treatment_notes,
            auth.token,
            setLoggedOut,
          );
          if (treatmentNoteURL) {
            const tempAnchor = document.createElement("a");
            tempAnchor.download = "file.pdf";
            tempAnchor.target = "_blank";
            if (treatmentNoteURL.generated_notes_download_url) {
              tempAnchor.href = treatmentNoteURL.generated_notes_download_url;
              tempAnchor.click();
            }
            tempAnchor.remove();
          }
        }
        const submittedRequisition = await submitNewRequisitionOrder(
          convertedFormToType,
          auth.token,
          setLoggedOut,
        );
        // Track language selection if the user checked the phlebotomy box and selected a language
        if (
          submittedRequisition?.is_mobile_phlebotomy &&
          submittedRequisition?.preferred_language !== undefined
        ) {
          TrackAnalyticsEvent(
            "PhlebLanguageSelected",
            auth.token,
            auth.username,
            { preferred_language: submittedRequisition.preferred_language },
          );
        }

        if (submittedRequisition) {
          setSubmmittedRequisition(submittedRequisition);
          await RefreshNewRequisitionOrders(
            auth.token,
            setAllOrderedRequisitions,
            setLoggedOut,
          );
          setTab();
          setDraftEditReq(undefined);
          setGlobalNewRequisition(getInitialNewRequisitionForm());
          setSubmitLoading(false);
          setActiveStep((activeStep: number) => activeStep + 1);
        } else {
          setSubmitLoading(false);
          setIsSubmitError(true);
        }
      } catch (e) {
        //
      }
    }
  };

  return (
    <PreFlightWrapper>
      {submitLoading ? (
        <div
          style={{
            height: "100%",
            width: "100%",
          }}
        >
          <SpinnerLoading />
        </div>
      ) : (
        <>
          <PatientInformation preflight />
          <ClinicalIndication preflight />
          <NewReqBillingInformation preflight />
          {shipKitEnabled && <NewReqShippingInformation preflight />}
          <br />
          <br />
          <br />
          <br />
          <PreFlightCheckButtons>
            <Button
              id="new-test-back"
              label="BACK"
              onClick={() => {
                setIsSubmitError(false);
                setActiveStep((activeStep: number) => {
                  const previous = activeStep - 1;
                  if (previous === StepperIndex.Shipping && !shipKitEnabled) {
                    return previous - 1;
                  }
                  return previous;
                });
              }}
              size="medium"
              reverse
            />
            <Button
              id="new-test-confirm-and-send"
              label="CONFIRM & SEND"
              onClick={async () => {
                await handleSubmit();
              }}
              size="medium"
            />
          </PreFlightCheckButtons>
        </>
      )}
    </PreFlightWrapper>
  );
};
export default PreflightCheck;
